/**
 * The run/stop button's own tiny state machine, its adaptive polling, and the manual-trigger
 * mutations that drive it (ACT2-511 Phase 3/4).
 *
 * Ported from rudder-webapp's `SyncDetailsStore.resolveButtonState` / `runSyncButton.tsx`, with one
 * behavioral fix over the original — see BUG FIX #1 in the effect below.
 *
 * This hook keeps its OWN `listSyncRuns` read (`useLatestRun`, `limit: 1`) even though `RunsPanel`
 * (`components/RunsPanel.tsx`) already holds a full page of the same sync's runs on screen — a task-6
 * fold ("pass the caller's newest run in instead") was evaluated and reverted before landing. Two
 * things make the fold non-trivial, not just "an extra prop":
 *
 * 1. The settle effect below is gated on `latest.settledAt` CHANGING, not on the run value — that is
 *    the whole point of BUG FIX #1 (a poll that legitimately returns no run still has to be observed
 *    as a completed fetch). `usePastRuns` (`hooks/usePastRuns.ts`) exposes no such signal today,
 *    so accepting its `runs[0]` in place of `latestRun` would silently drop that gate — the machine
 *    would then react to a stale value sticking around rather than to a fetch actually completing.
 * 2. The two polls are not the same cadence by design: this hook tightens to
 *    `TRANSITION_POLL_INTERVAL_MS` (3s) while a start/stop is confirming, and eases back to
 *    `BASE_POLL_INTERVAL_MS` (10s) once it settles (`runPollIntervalMs`). Folding onto `usePastRuns`'s
 *    own poll means riding `pastRunsPollIntervalMs` instead (verified against `hooks/usePastRuns.ts`) —
 *    and that function has no tightened branch: it answers `BASE_POLL_INTERVAL_MS` (10s) while `live`
 *    or a listed run is running, and `SETTLED_POLL_INTERVAL_MS` (60s) otherwise. The 3s transition
 *    cadence would not move to the shared poll, it would stop existing — a start/stop confirmation
 *    would take up to 10s to observe instead of 3s, silently, the day this fold landed.
 *
 * The two polls now agree about idle — both ease to `SETTLED_POLL_INTERVAL_MS`, which is why that
 * constant lives here beside the other two rather than in `usePastRuns`. What is still not shared is
 * the TRANSITION cadence, and that is the whole of what this note is defending.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';

import { isRunning } from '../lib/run-view';
import type { RawSyncRun } from '../types';
import { useLatestRun } from './useLatestRun';

export type RunButtonState = 'idle' | 'starting' | 'running' | 'stopping';

/** Cadence for a run that is actually MOVING — metrics change, so the numbers must keep up. */
export const BASE_POLL_INTERVAL_MS = 10_000;
/** Tightened cadence while a start or stop is confirming. `running` is deliberately NOT here —
 * it stays on the base cadence, the detail people porting this get wrong. */
export const TRANSITION_POLL_INTERVAL_MS = 3_000;
/**
 * Cadence when nothing is happening at all. Shared with `usePastRuns` so the header and the table
 * agree about what "idle" costs — they poll the SAME endpoint, and it made no sense for the header to
 * sit at 10s forever while the table it heads eased to 60s.
 *
 * The trade is explicit: a run started ELSEWHERE (the schedule, another tab, another person) is now
 * discovered in up to 60s rather than up to 10s. That is the same latency the settled table already
 * accepts for the same event, and nothing on screen is moving while we wait.
 */
export const SETTLED_POLL_INTERVAL_MS = 60_000;

export function runPollIntervalMs(state: RunButtonState): number {
  if (state === 'starting' || state === 'stopping') return TRANSITION_POLL_INTERVAL_MS;
  return state === 'running' ? BASE_POLL_INTERVAL_MS : SETTLED_POLL_INTERVAL_MS;
}

/**
 * Pure. Ported from rudder-webapp's `SyncDetailsStore.resolveButtonState` — runs on every poll
 * result, never on the mutation's own `isPending` (see `useRunSync`: the poll is what confirms a
 * transition, not the request that kicked it off).
 */
export function nextRunButtonState(current: RunButtonState, latestRunIsRunning: boolean): RunButtonState {
  if (current === 'starting') return latestRunIsRunning ? 'running' : 'starting';
  if (current === 'stopping') return latestRunIsRunning ? 'stopping' : 'idle';
  return latestRunIsRunning ? 'running' : 'idle';
}

/**
 * The escape `nextRunButtonState` does not have, applied on top of it.
 *
 * `'starting'` above only leaves on seeing a RUNNING run, so a run that finishes inside a single
 * poll window — routine on a small audience, where a sync completes in about a second — is never
 * observed live and pins the button on "Starting…" for good. The ported store has the same hole;
 * it stays invisible there because the webapp's runs are slower.
 *
 * A run whose id differs from the one that existed when Start was pressed IS the run we asked for,
 * so its state answers the question whether or not we ever caught it mid-flight.
 */
export function resolveStartedState(
  current: RunButtonState,
  runIdBeforeStart: string | undefined,
  latestRun: RawSyncRun | undefined,
): RunButtonState {
  const running = isRunning(latestRun);
  if (current === 'starting' && latestRun !== undefined && latestRun.run_id !== runIdBeforeStart) {
    return running ? 'running' : 'idle';
  }
  return nextRunButtonState(current, running);
}

/**
 * Deliberately narrow. This hook's own `useLatestRun` read carries `latestRun` / `isLoading` / `error`
 * / `refetch` internally, but nothing under `ui/src/activations` reads any of the four off the RETURN
 * value any more — `ActivationDetail` (the only caller) consumes only `state`, `userStartedRun`,
 * `startSync`, `stopSync`.
 * They had production readers before the Overview tab was deleted (it rendered `latestRun` as its own
 * card, `isLoading` as its own skeleton, and `error` as its own "Could not load runs" retry state); all
 * three went with the tab, and re-exporting them here now would be an interface advertising values
 * nobody reads.
 *
 * The dropped `error` specifically: this hook's poll can still fail like any query, and that failure is
 * now surfaced NOWHERE — a bare miss, not a fold. It is left that way on purpose rather than wired to a
 * toast: the poll is a background heartbeat with no user-visible "last checked" affordance and no retry
 * control of its own, so a failure toast would be reporting a fetch the reader never asked for by name.
 * A transient failure just means `state` stays parked on whatever it last confirmed until the NEXT
 * scheduled poll succeeds — the machine doesn't regress, it stalls. `RunsPanel`'s own `ListError`
 * already tells the reader their run history failed to load off the same backend call (a different
 * query — `listSyncRuns` paged, vs. this hook's `limit: 1` — so not guaranteed to fail together, but in
 * practice the same endpoint going down takes both). The residual, real gap: if THIS hook's poll fails
 * independently of the table's, the button silently stops advancing with no signal anywhere on screen.
 * See the Task 8 report.
 */
export interface RunSync {
  state: RunButtonState;
  /**
   * THIS reader pressed Run sync, and the run they asked for has not settled yet.
   *
   * `state !== 'idle'` cannot answer that question: `nextRunButtonState` returns `'running'` for a
   * run this button never asked for — a cron fire is `'idle'` → `'running'` (this file's own settle
   * effect says as much where it excludes exactly that case from invalidating the syncs list). The
   * run table's "follow the new run" rule needs the narrower fact, or a scheduled sync firing while
   * someone studies an old failed run drags the detail pane off it, silently and via `replace`.
   *
   * True from `startSync` until the machine returns to `'idle'` (or the start request fails), which
   * for a manual start is exactly the window `state !== 'idle'` covers — the narrowing removes the
   * externally-triggered case and nothing else.
   */
  userStartedRun: boolean;
  /** Defaults to `incremental`; pass `full` for an explicit full re-sync. */
  startSync: (syncType?: 'full' | 'incremental') => void;
  stopSync: () => void;
}

export function useRunSync(syncId: string): RunSync {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [state, setState] = useState<RunButtonState>('idle');
  // State, not a ref: `RunsPanel` reads it through a prop, so a change has to re-render.
  const [userStartedRun, setUserStartedRun] = useState(false);
  const latest = useLatestRun(syncId, runPollIntervalMs(state));
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const signIn = useRudderstackSignInAgain();

  const startMutation = useMutation(
    orpc.activations.startSyncRun.mutationOptions({
      onError: (err) => {
        setState('idle');
        setUserStartedRun(false);
        if (!signIn.toastIfReauth(err)) toast.error(err.message || 'Failed to start sync');
        // Every start failure refetches the latest run, not just a CONFLICT — a start can fail for
        // reasons that have nothing to do with another run (auth, network, a validation error), and
        // this refetch is harmless on all of them. It earns its keep on CONFLICT specifically, which
        // covers two different platform states behind one status: "already running" (a run is moving
        // elsewhere — the schedule, another tab, another person) and "not ready yet" (the data plane
        // hasn't registered the sync). For "already running", the refetch lets the button move to
        // `running` off this response instead of waiting up to `SETTLED_POLL_INTERVAL_MS` (60s) for
        // the idle poll to notice. For "not ready yet" there is nothing new to find, so the refetch is
        // a no-op beyond the extra request — harmless, not a fix for that case.
        latest.refetch();
      },
    }),
  );
  const stopMutation = useMutation(
    orpc.activations.stopSyncRun.mutationOptions({
      onError: (err) => {
        setState('idle');
        if (!signIn.toastIfReauth(err)) toast.error(err.message || 'Failed to stop sync');
      },
    }),
  );

  // Tracks which settle this effect has already processed, so the effect can also depend on
  // `state` (to read it fresh, without a stale closure) without re-processing on a state-only
  // render — e.g. `startSync` flipping `state` to 'starting' should not, by itself, re-run the
  // settle logic below against the previous poll's data.
  const processedSettledAtRef = useRef(0);
  // The run that existed at the moment Start was pressed, so a NEW run can be told from the old one
  // even if we never catch it running. See `resolveStartedState`.
  const runIdBeforeStartRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (latest.settledAt === 0 || latest.settledAt === processedSettledAtRef.current) return;
    processedSettledAtRef.current = latest.settledAt;

    // BUG FIX #1 (the whole point of this hook): rudder-webapp's `useLatestSyncRunFetcher` guards
    // `if (latestRunData) setLatestRun(...)`, so a poll that legitimately returns NO run at all
    // (e.g. the run has aged out of the retention window mid-stop) never updates — a 'stopping'
    // button never clears. Gating on the *request having succeeded* (`settledAt` changing) rather
    // than on the value being truthy fixes it: `isRunning(undefined)` is `false`, which is exactly
    // the signal a 'stopping' → 'idle' transition needs.
    const next = resolveStartedState(state, runIdBeforeStartRef.current, latest.run);
    if (next !== state) {
      // A poll result arriving is an external event React does not model — the confirmed
      // transition can only be observed once the fetch that reports it has settled.
      setState(next);
    }

    // Invalidate the syncs list exactly when a transition COMPLETES — the moment the list's
    // `lastRun` has actually changed upstream, not the moment the button was clicked. Comparing
    // the previous state (not just the new value) is what excludes an externally-triggered run
    // (e.g. 'idle' → 'running' from a cron fire) from invalidating on this button's behalf.
    // Whatever this reader asked for is over the moment the machine parks — including the escape
    // `resolveStartedState` provides for a run that finished inside one poll window. Unconditional
    // rather than guarded on the current value: React bails out on a set-to-same-value, and reading
    // `userStartedRun` here would only add a dependency for nothing.
    if (next === 'idle') setUserStartedRun(false);

    if ((state === 'starting' && next !== 'starting') || (state === 'stopping' && next === 'idle')) {
      void queryClient.invalidateQueries({ queryKey: orpc.activations.listSyncsForSource.key() });
    }
  }, [latest.settledAt, latest.run, state, queryClient, orpc]);

  function startSync(syncType: 'full' | 'incremental' = 'incremental'): void {
    runIdBeforeStartRef.current = latest.run?.run_id;
    setState('starting');
    setUserStartedRun(true);
    // Incremental remains the primary/default path; full sync is only reached by the explicit
    // confirmed action in the detail header.
    startMutation.mutate({ syncId, syncType });
  }

  function stopSync(): void {
    setState('stopping');
    stopMutation.mutate({ syncId });
  }

  return {
    state,
    userStartedRun,
    startSync,
    stopSync,
  };
}
